import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from '../contexts/SocketContext';
import type { ConsoleOutputLine, GenerationStatus } from '../types/generation';

interface UseGenerationSocketOptions {
  eventPrefix: string;
  schemaId: string | undefined;
  onComplete?: (data: Record<string, unknown>) => void;
}

export const useGenerationSocket = ({ eventPrefix, schemaId, onComplete }: UseGenerationSocketOptions) => {
  const [generating, setGenerating] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState<ConsoleOutputLine[]>([]);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
  const { addEventListener } = useSocket();

  // Keep refs for values used inside event handlers to avoid re-registering on every change
  const schemaIdRef = useRef(schemaId);
  schemaIdRef.current = schemaId;

  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const cleanups = [
      addEventListener(`${eventPrefix}-start`, (data: { schemaId: string }) => {
        if (data.schemaId === schemaIdRef.current) {
          setConsoleOutput([]);
          setGenerationStatus('running');
        }
      }),
      addEventListener(
        `${eventPrefix}-output`,
        (data: { schemaId: string; type: 'stdout' | 'stderr'; data: string }) => {
          if (data.schemaId === schemaIdRef.current) {
            setConsoleOutput((prev) => [...prev, { type: data.type, data: data.data }]);
          }
        },
      ),
      addEventListener(
        `${eventPrefix}-complete`,
        (data: { schemaId: string; success: boolean; [key: string]: unknown }) => {
          if (data.schemaId === schemaIdRef.current) {
            setGenerating(false);
            setGenerationStatus(data.success ? 'success' : 'error');
            onCompleteRef.current?.(data);
          }
        },
      ),
      addEventListener(`${eventPrefix}-error`, (data: { schemaId: string; error: string }) => {
        if (data.schemaId === schemaIdRef.current) {
          setGenerating(false);
          setGenerationStatus('error');
          setConsoleOutput((prev) => [...prev, { type: 'stderr', data: `Error: ${data.error}` }]);
        }
      }),
    ];

    return () => {
      for (const cleanup of cleanups) cleanup();
    };
  }, [eventPrefix, addEventListener]);

  const startGeneration = useCallback((initialMessage: string) => {
    setGenerating(true);
    setConsoleOutput([{ type: 'stdout', data: initialMessage }]);
    setGenerationStatus(null);
  }, []);

  const failGeneration = useCallback((message: string) => {
    setGenerating(false);
    setGenerationStatus('error');
    setConsoleOutput([{ type: 'stderr', data: message }]);
  }, []);

  const stopGeneration = useCallback(() => {
    setGenerating(false);
    setGenerationStatus('stopped');
    setConsoleOutput((prev) => [...prev, { type: 'stderr', data: 'Stopped by user' }]);
  }, []);

  return {
    generating,
    consoleOutput,
    generationStatus,
    startGeneration,
    failGeneration,
    stopGeneration,
  };
};
